import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { PageHeader } from "@/components/primitives";
import { createPlant } from "@/lib/api";
import { toast } from "sonner";
import { Camera, ImagePlus, Flower2, Save, X } from "lucide-react";

const CATEGORIES = ["dahlia", "rose", "tulip", "vegetable", "herb", "other"];
const TYPES = ["seed", "tuber", "cutting", "bulb", "plant"];
const STAGES = [
  { key: "purchased", dot: "#7E8C5B" },
  { key: "planted", dot: "#A4543A" },
  { key: "germinated", dot: "#5F6B43" },
  { key: "flowering", dot: "#C26B4A" },
  { key: "dormant", dot: "#8A8474" },
  { key: "finished", dot: "#5A5448" },
];

const empty = {
  name: "",
  category: "dahlia",
  type: "tuber",
  variety: "",
  color: "",
  vendor: "",
  purchase_date: "",
  cost: "",
  quantity: 1,
  height_cm: "",
  stage: "purchased",
  planted_date: "",
  germinated_date: "",
  first_bloom_date: "",
  position: "",
  notes: "",
  favorite: false,
  photos: [],
};

export default function NewSpecimen() {
  const nav = useNavigate();
  const loc = useLocation();
  const prefill = loc.state?.prefill || null;
  const [form, setForm] = useState(prefill ? { ...empty, ...prefill } : empty);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const MAX_BYTES = 1_400_000; // ~1.4 MB — server caps around 1.86 MB after b64
  const pickPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const added = [];
    for (const f of files) {
      if (!f.type.startsWith("image/")) { toast.error(`${f.name} isn't an image`); continue; }
      if (f.size > MAX_BYTES) { toast.error(`${f.name} is too large — try under 1.4 MB`); continue; }
      try {
        const data = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result);
          r.onerror = rej;
          r.readAsDataURL(f);
        });
        added.push(data);
      } catch {
        toast.error(`Couldn't read ${f.name}`);
      }
    }
    if (added.length) {
      set("photos", [...(form.photos || []), ...added]);
      toast.success(`${added.length} photo${added.length > 1 ? "s" : ""} added`);
    }
  };
  const removePhoto = (i) => set("photos", (form.photos || []).filter((_, j) => j !== i));

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name.trim()) {
      toast.error("A specimen needs a name");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        cost: form.cost === "" ? null : Number(form.cost),
        quantity: form.quantity === "" || form.quantity == null ? 1 : Math.max(1, Math.floor(Number(form.quantity))),
        height_cm: form.height_cm === "" || form.height_cm == null ? null : Math.max(0, Math.floor(Number(form.height_cm))),
        purchase_date: form.purchase_date || null,
        planted_date: form.planted_date || null,
        germinated_date: form.germinated_date || null,
        first_bloom_date: form.first_bloom_date || null,
      };
      const created = await createPlant(payload);
      toast.success(`${created.name} entered the patch`);
      nav(`/garden/${created.id}`);
    } catch (err) {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="new-specimen-page">
      <PageHeader
        kicker={prefill ? "From the Catalogue" : "New Entry"}
        title="New Specimen"
        subtitle={prefill ? `Pre-filled from ${prefill.vendor || "Heritage Dahlias"}` : "A seed, tuber, or cutting"}
      />

      {/* Photo picker + gallery */}
      {form.photos && form.photos.length > 0 ? (
        <div className="mt-2 h-card p-3" data-testid="specimen-photo-preview">
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {form.photos.map((src, i) => (
              <div key={i} className="relative aspect-square overflow-hidden rounded-md border border-heritage-border bg-cream-deep" data-testid={`specimen-photo-${i}`}>
                <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
                <button
                  type="button"
                  onClick={() => removePhoto(i)}
                  className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
                  aria-label="Remove photo"
                  data-testid={`specimen-photo-remove-${i}`}
                >
                  <X size={12} />
                </button>
                {i === 0 && (
                  <span className="absolute bottom-1 left-1 text-[10px] uppercase tracking-[0.18em] bg-black/60 text-cream-soft px-1.5 py-0.5 rounded">
                    Cover
                  </span>
                )}
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs italic text-ink-soft text-center">
            First photo becomes the cover. Add more or remove any before saving.
          </p>
        </div>
      ) : (
        <div className="mt-2 h-card border-dashed bg-cream-deep/40 rounded-lg p-6 flex flex-col items-center justify-center text-center" data-testid="specimen-photo-area">
          <Flower2 size={36} className="text-sage-dark" />
          <p className="mt-2 font-display italic text-ink-soft text-lg">Add photos</p>
          <p className="text-xs text-ink-soft mt-1">Pick from your gallery, or snap one on your phone. Up to ~1.4&nbsp;MB each.</p>
        </div>
      )}
      <div className="mt-3 grid grid-cols-2 gap-3">
        <label className="btn-ghost cursor-pointer" data-testid="specimen-gallery-btn">
          <ImagePlus size={16} /> Add from Gallery
          <input type="file" accept="image/*" multiple className="hidden" onChange={pickPhotos} data-testid="specimen-gallery-input" />
        </label>
        <label className="btn-ghost cursor-pointer" data-testid="specimen-camera-btn">
          <Camera size={16} /> Camera
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={pickPhotos} data-testid="specimen-camera-input" />
        </label>
      </div>

      {/* THE BASICS */}
      <Section label="The Basics">
        <Field label="Name *">
          <input
            className="field"
            placeholder="e.g., Café au Lait"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            data-testid="specimen-name"
            required
          />
        </Field>
        <div>
          <p className="text-sm text-ink-soft mb-2">Category</p>
          <Chips
            options={CATEGORIES.map((c) => ({ key: c, label: cap(c) }))}
            value={form.category}
            onChange={(v) => set("category", v)}
            testid="specimen-category"
          />
        </div>
        <div>
          <p className="text-sm text-ink-soft mb-2">Type</p>
          <Chips
            options={TYPES.map((c) => ({ key: c, label: cap(c) }))}
            value={form.type}
            onChange={(v) => set("type", v)}
            testid="specimen-type"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Variety">
            <input
              className="field"
              placeholder="e.g., Dinnerplate"
              value={form.variety}
              onChange={(e) => set("variety", e.target.value)}
              data-testid="specimen-variety"
            />
          </Field>
          <Field label="Color">
            <input
              className="field"
              placeholder="e.g., Pink"
              value={form.color}
              onChange={(e) => set("color", e.target.value)}
              data-testid="specimen-color"
            />
          </Field>
        </div>
        <Field label="How many of this specimen do you have?">
          <input
            type="number" min="1" max="10000" step="1"
            className="field"
            placeholder="1"
            value={form.quantity}
            onChange={(e) => set("quantity", e.target.value)}
            data-testid="specimen-quantity"
          />
          <span className="text-xs italic text-ink-soft mt-1 inline-block">
            One record can represent a whole row of the same variety.
          </span>
        </Field>
        <Field label="Plant height (cm)">
          <input
            type="number" min="0" max="500" step="1"
            className="field"
            placeholder="e.g. 120"
            value={form.height_cm}
            onChange={(e) => set("height_cm", e.target.value)}
            data-testid="specimen-height"
          />
          <span className="text-xs italic text-ink-soft mt-1 inline-block">
            How tall the plant grows at maturity.
          </span>
        </Field>
      </Section>

      {/* PURCHASE DETAILS */}
      <Section label="Purchase Details">
        <Field label="Vendor / Source">
          <input
            className="field"
            placeholder="e.g., Sarah Raven"
            value={form.vendor}
            onChange={(e) => set("vendor", e.target.value)}
            data-testid="specimen-vendor"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Purchase Date">
            <input
              type="date"
              className="field"
              value={form.purchase_date}
              onChange={(e) => set("purchase_date", e.target.value)}
              data-testid="specimen-purchase-date"
            />
          </Field>
          <Field label="Cost (NZ$)">
            <input
              type="number"
              step="0.01"
              className="field"
              placeholder="0.00"
              value={form.cost}
              onChange={(e) => set("cost", e.target.value)}
              data-testid="specimen-cost"
            />
          </Field>
        </div>
      </Section>

      {/* GROWTH TIMELINE */}
      <Section label="Growth Timeline">
        <div>
          <p className="text-sm text-ink-soft mb-2">Current Stage</p>
          <Chips
            options={STAGES.map((s) => ({ key: s.key, label: cap(s.key), dot: s.dot }))}
            value={form.stage}
            onChange={(v) => set("stage", v)}
            testid="specimen-stage"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Planted">
            <input type="date" className="field" value={form.planted_date} onChange={(e) => set("planted_date", e.target.value)} data-testid="specimen-planted-date" />
          </Field>
          <Field label="Germinated">
            <input type="date" className="field" value={form.germinated_date} onChange={(e) => set("germinated_date", e.target.value)} data-testid="specimen-germinated-date" />
          </Field>
        </div>
        <Field label="First Bloom">
          <input type="date" className="field" value={form.first_bloom_date} onChange={(e) => set("first_bloom_date", e.target.value)} data-testid="specimen-first-bloom" />
        </Field>
      </Section>

      {/* LOCATION */}
      <Section label="Location">
        <Field label="Position (optional)">
          <input
            className="field"
            placeholder="e.g., Bed B, row 2"
            value={form.position}
            onChange={(e) => set("position", e.target.value)}
            data-testid="specimen-position"
          />
        </Field>
        <p className="text-xs italic text-ink-soft -mt-1">
          Tip: create garden beds from the Beds tab to assign plants here.
        </p>
      </Section>

      {/* NOTES */}
      <Section label="The Gardener's Notes">
        <textarea
          rows={4}
          className="field resize-y"
          placeholder="Observations, weather, suspicions of slugs..."
          value={form.notes}
          onChange={(e) => set("notes", e.target.value)}
          data-testid="specimen-notes"
        />
      </Section>

      <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-end">
        <button type="button" className="btn-ghost" onClick={() => nav(-1)} data-testid="specimen-cancel">
          Cancel
        </button>
        <button type="submit" className="btn-heritage" disabled={saving} data-testid="specimen-save">
          <Save size={16} />
          {saving ? "Saving..." : "Save Specimen"}
        </button>
      </div>
    </form>
  );
}

function cap(s) { return s.charAt(0).toUpperCase() + s.slice(1); }

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-sm text-ink-soft mb-1.5 inline-block">{label}</span>
      {children}
    </label>
  );
}

function Section({ label, children }) {
  return (
    <section className="mt-8">
      <div className="kicker mb-4">{label}</div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Chips({ options, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          className="chip"
          data-active={value === o.key}
          data-testid={`${testid}-${o.key}`}
          onClick={() => onChange(o.key)}
        >
          {o.dot && <span className="inline-block w-2 h-2 rounded-full" style={{ background: o.dot }} />}
          {o.label}
        </button>
      ))}
    </div>
  );
}
